import React, { FC, Fragment } from 'react';
import Svg from 'svg-inline-react';
import { get, find, map, isEqual } from 'lodash';
import SectionTitle from '@src/components/SectionTitle';
import DescriptionField from '@src/components/DescriptionField';
import MediaUpload from '@src/components/MediaUpload';
import FormValidationError from '@src/components/FormValidationError';
import { Container } from '@src/components/Container';
import { TextField } from '@src/components/TextField';
import Preview from '@src/components/Preview';
import { Wrapper } from '@src/components/Wrapper';
import { COMPANY_ROUTE } from '@src/components/Preview/appRoutes';
import VideoPurpleSvg from '@src/svgs/video-purple.svg';
import { capitalize } from '@src/util/capitalize';
import { trackEvent, trackUpdateCompanyProfile } from '@src/metrics';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { ContainerType } from '@src/components/Container/enums/containerType.enum';
import { ImageType } from '@src/graphql/generated';
import { LocationList } from '../LocationList';
import { Benefits } from '../Benefits';
import Media from '../Media';
import Publish from '../Publish';
import ClientsList from '../ClientsList';
import Section from '../Section';
import Menu from '../Menu';
import { Markets } from '../Markets';
import { CompanySize } from '../CompanySize';
import { CompanyTypes } from '../CompanyTypes';
import { Revenue } from '../Revenue';
import { Industries } from '../Industries';
import { Funding } from '../Funding';
import { LatestFunding } from '../LatestFunding';
import { ViewProps } from './props';
import '../styles.scss';

const MENU_ITEM_IDS = ['main', 'facts', 'what-you', 'offices', 'markets', 'social'];

const SOCIAL_MEDIA_INPUTS = [
  { name: 'website', placeHolder: 'Company website' },
  { name: 'facebook', placeHolder: 'Facebook' },
  { name: 'twitter', placeHolder: 'Twitter' },
  { name: 'linkedin', placeHolder: 'LinkedIn' },
  { name: 'instagram', placeHolder: 'Instagram' },
];

export const View: FC<ViewProps> = ({
  data,
  setImage,
  onFieldChange,
  benefits,
  companyTypes,
  companySizes,
  companyFundings,
  companyRevenues,
  setGalleryImage,
  onGalleryImageRemove,
  onLinkChange,
  recruiter,
  publishProfile,
  isPublishing,
  errors,
  contentRef,
  setScroll,
  setContentRef,
  onCompanyFieldChange,
  company,
  minMissionLength,
}) => {
  const hasErrors = !isEqual(errors, {});

  const mapImages = (images?: Pick<ImageType, 'fullUrl'>[]) => {
    return images?.map(image => ({ ...image, url: image.fullUrl })) || [];
  };

  return (
    <Wrapper className="company-form">
      <Preview companyProfile={data} url={COMPANY_ROUTE} />

      <div className="company-form-content-wrapper">
        <Container className="page-content" type={ContainerType.BORDERED}>
          <Menu contentRef={contentRef} data={data} menuIds={MENU_ITEM_IDS} setScroll={setScroll} />

          <Publish
            disabled={data?.isPublished || isPublishing}
            onClick={() => {
              publishProfile();

              trackEvent({
                context: MetricContext.UPDATING_COMPANY_PROFILE,
                interaction: MetricAction.PUBLISH_COMPANY_PROFILE,
              });
            }}
          >
            {isPublishing ? 'Publishing...' : data?.isPublished ? 'Saved' : 'Publish draft'}
          </Publish>

          <div className="company-form-content" ref={setContentRef}>
            <Section counter={1} id={MENU_ITEM_IDS[0]} title="Main information">
              {recruiter.isSiteAdmin ? (
                <>
                  <SectionTitle size="S" title="Company name*" />

                  <TextField
                    defaultValue={data?.name}
                    message={errors?.name}
                    messageType={errors?.name && 'error'}
                    onBlur={e =>
                      trackUpdateCompanyProfile({
                        'Company Name': e.target.value,
                      })
                    }
                    onChange={value => onFieldChange('name', value)}
                    placeholder="Please fill"
                    wrapperClassName="company-form-name"
                  />
                </>
              ) : (
                <>
                  <SectionTitle size="M" title={data?.name} />
                  <br />
                </>
              )}

              <DescriptionField
                className="company-form-mission"
                defaultValue={data?.missionStatement}
                invalid={!!errors?.missionStatement}
                maxLength={350}
                message={
                  errors?.missionStatement
                    ? `In order to prove better context for applicants the statement should be at least ${minMissionLength} characters long.`
                    : `Minimum ${minMissionLength} characters`
                }
                minLength={minMissionLength}
                onBlur={e =>
                  trackUpdateCompanyProfile({
                    'Mission Statement': e.target.value,
                  })
                }
                onChange={value => onFieldChange('missionStatement', value)}
                placeholder="Type here..."
                title="Mission statement *"
                titleSize="XS"
              />

              <div className="company-form-main-images">
                <MediaUpload
                  className="company-form-logo"
                  file={
                    data?.logo
                      ? {
                          ...data.logo,
                          url: data.logo.fullUrl,
                        }
                      : undefined
                  }
                  height={512}
                  invalid={!!errors?.logo}
                  minHeight={256}
                  minWidth={256}
                  onRemove={() => {
                    onFieldChange('logo', null);

                    trackUpdateCompanyProfile({
                      'Logo or Icon': '',
                    });
                  }}
                  onUpload={image => {
                    setImage('logo', image);

                    trackUpdateCompanyProfile({
                      'Logo or Icon': image.fullUrl,
                    });
                  }}
                  single
                  title="Logo or icon*"
                  titleSize="XS"
                  width={512}
                />

                <MediaUpload
                  className="company-form-header"
                  file={
                    data?.headerImage
                      ? {
                          ...data.headerImage,
                          url: data.headerImage.fullUrl,
                        }
                      : undefined
                  }
                  height={500}
                  invalid={!!errors?.headerImage}
                  minHeight={256}
                  minWidth={512}
                  onRemove={() => {
                    onFieldChange('headerImage', null);
                    trackUpdateCompanyProfile({
                      'Header Image': '',
                    });
                  }}
                  onUpload={image => {
                    setImage('headerImage', image);
                    trackUpdateCompanyProfile({
                      'Header Image': image.fullUrl,
                    });
                  }}
                  single
                  title="Header image*"
                  titleSize="XS"
                  width={1024}
                />
              </div>

              <SectionTitle size="S" title="Company video" />

              <div className="company-form-video-wrapper">
                <Svg src={VideoPurpleSvg} />

                <TextField
                  clearable
                  defaultValue={get(data, 'video.rawUrl')}
                  onBlur={e =>
                    trackUpdateCompanyProfile({
                      'Company Video': e.target.value,
                    })
                  }
                  onChange={value => {
                    onFieldChange('video', { rawUrl: value });

                    if (!value) {
                      trackUpdateCompanyProfile({
                        'Youtube or Vimeo URL': '',
                      });
                    }
                  }}
                  placeholder="Youtube or Vimeo url"
                  wrapperClassName="company-form-video"
                />
              </div>
            </Section>

            <Section counter={2} id={MENU_ITEM_IDS[1]} title="Facts about the company">
              <SectionTitle size="S" title="Year founded" />

              <TextField
                clearable
                defaultValue={data?.founded}
                message={errors?.founded}
                messageType={errors?.founded && 'error'}
                onBlur={e =>
                  trackUpdateCompanyProfile({
                    'Year Founded': e.target.value,
                  })
                }
                onChange={value => {
                  onFieldChange('founded', value ? Number(value) : undefined);

                  if (!value) {
                    trackUpdateCompanyProfile({
                      'Year Founded': '',
                    });
                  }
                }}
                placeholder="Please fill"
                wrapperClassName="company-form-video"
              />

              <CompanySize
                data={companySizes}
                isInvalid={!!errors?.companySize}
                onChange={value => {
                  onFieldChange('companySize', value);
                  trackUpdateCompanyProfile({
                    'Company Size': value.name,
                  });
                }}
                value={data?.companySize || undefined}
              />

              <CompanyTypes
                data={companyTypes}
                isInvalid={!!errors?.companyTypes}
                onChange={value => {
                  onCompanyFieldChange('companyTypes', value);

                  trackUpdateCompanyProfile({
                    'Company Types': value.map(type => type.name),
                  });
                }}
                value={company.companyTypes}
              />

              <Revenue
                data={companyRevenues}
                defaultValue={data?.revenue || undefined}
                isInvalid={!!errors?.revenue}
                onChange={revenue => {
                  onFieldChange('revenue', revenue);

                  trackUpdateCompanyProfile({
                    Revenue: revenue.value || 'None',
                  });
                }}
              />

              <Industries
                onChange={industries => {
                  onCompanyFieldChange('industries', industries);

                  trackUpdateCompanyProfile({
                    Industries: industries?.map(industry => industry.name),
                  });
                }}
                value={company.industries}
              />

              <Funding
                data={companyFundings}
                defaultValue={data?.funding || undefined}
                isInvalid={!!errors?.funding}
                onChange={value => {
                  onFieldChange('funding', value);

                  trackUpdateCompanyProfile({
                    Funding: value.value || 'None',
                  });
                }}
              />

              <LatestFunding
                data={data?.latestFunding || undefined}
                onChange={value => {
                  onFieldChange('latestFunding', value);
                }}
              />
            </Section>

            <Section counter={3} id={MENU_ITEM_IDS[2]} title="What do you do?">
              <DescriptionField
                defaultValue={data?.serviceDescription}
                invalid={!!errors?.serviceDescription}
                maxLength={750}
                onBlur={e =>
                  trackUpdateCompanyProfile({
                    Description: e.target.value,
                  })
                }
                onChange={value => onFieldChange('serviceDescription', value)}
                placeholder="Type here..."
                title="Description *"
                titleSize="XS"
              />

              <MediaUpload
                className="company-form-media"
                files={mapImages(data?.productImages)}
                minHeight={256}
                minWidth={256}
                onRemove={image => onGalleryImageRemove('productImages', image)}
                onUpload={image => setGalleryImage('productImages', image)}
                placeholder={<strong>Add up to 10 images. Max size 10 MB. 720x450px.</strong>}
                title="Images showing the product/service"
                titleSize="XS"
              />
            </Section>

            <Section counter={4} id={MENU_ITEM_IDS[3]} title="Offices">
              <DescriptionField
                defaultValue={data?.officeDescription}
                maxLength={750}
                onBlur={e =>
                  trackUpdateCompanyProfile({
                    'Office Description': e.target.value,
                  })
                }
                onChange={value => onFieldChange('officeDescription', value)}
                placeholder="Type here..."
                title="About the offices"
                titleSize="XS"
              />

              <SectionTitle size="S" title="Office locations*" />

              <LocationList
                shouldClearOnSelect
                isInvalid={!!errors?.locations}
                defaultValues={data?.locations}
                onLocationSelected={locations => {
                  onFieldChange('locations', locations);

                  trackUpdateCompanyProfile({
                    Locations: locations.map(location => location.longName),
                  });
                }}
              />

              <SectionTitle size="S" title="Office or team video" />

              <div className="company-form-video-wrapper">
                <Svg src={VideoPurpleSvg} />
                <TextField
                  clearable
                  defaultValue={get(data, 'officeVideo.rawUrl')}
                  onBlur={e =>
                    trackUpdateCompanyProfile({
                      'Office Video': e.target.value,
                    })
                  }
                  onChange={value => {
                    onFieldChange('officeVideo', { rawUrl: value });

                    if (!value) {
                      trackUpdateCompanyProfile({
                        'Office Video': '',
                      });
                    }
                  }}
                  placeholder="Youtube or Vimeo url"
                  wrapperClassName="company-form-video"
                />
              </div>

              <MediaUpload
                className="company-form-media"
                files={mapImages(data?.officeImages)}
                minHeight={256}
                minWidth={256}
                onRemove={image => onGalleryImageRemove('officeImages', image)}
                onUpload={image => setGalleryImage('officeImages', image)}
                placeholder={<strong>Add up to 10 images. Max size 10 MB. 720x450px.</strong>}
                title="Office or team photos"
                titleSize="XS"
              />

              <Benefits
                data={benefits}
                defaultValue={data?.benefits}
                onChange={benefits => {
                  onFieldChange('benefits', benefits);

                  trackUpdateCompanyProfile({
                    Benefits: benefits.map(benefit => benefit.value),
                  });
                }}
              />
            </Section>

            <Section counter={5} id={MENU_ITEM_IDS[4]} title="Markets and clients">
              <Markets data={data} onChange={onFieldChange} />

              <DescriptionField
                className="company-form-clients-description"
                defaultValue={data?.clientsDescription}
                maxLength={500}
                onBlur={e =>
                  trackUpdateCompanyProfile({
                    'Clients Description': e.target.value,
                  })
                }
                onChange={value => onFieldChange('clientsDescription', value)}
                placeholder="Type here..."
                title="About our clients"
                titleSize="XS"
              />

              <SectionTitle size="S" title="Client logos" />

              <ClientsList
                defaultValues={data?.clients}
                onChange={value => {
                  onFieldChange('clients', value);

                  trackUpdateCompanyProfile({
                    'Client Logos': map(value, 'logo.fullUrl'),
                  });
                }}
              />
            </Section>

            <Section counter={6} id={MENU_ITEM_IDS[5]} title="Social media and articles">
              <SectionTitle
                size="S"
                subtitle="Where can people learn more and follow your company"
                title="Links and social media"
              />

              {SOCIAL_MEDIA_INPUTS.map(({ name, placeHolder }) => (
                <TextField
                  clearable
                  defaultValue={(find(data?.socialMedia, { source: name }) || {}).url}
                  key={name}
                  onBlur={e =>
                    trackUpdateCompanyProfile({
                      [capitalize(name)]: e.target.value,
                    })
                  }
                  onChange={value => {
                    onLinkChange(name, value);

                    if (!value) {
                      trackUpdateCompanyProfile({
                        [capitalize(name)]: '',
                      });
                    }
                  }}
                  placeholder={placeHolder}
                  wrapperClassName="social-input"
                />
              ))}

              <br />

              <SectionTitle
                size="S"
                subtitle="Are there any articles you would like to point out"
                title="Media publications"
              />

              <Media
                className="company-form-links-media"
                defaultValues={data?.media}
                onChange={value => {
                  onFieldChange('media', value);
                  trackUpdateCompanyProfile({
                    'Media Publications': map(value, 'url'),
                  });
                }}
              />
            </Section>

            {hasErrors && <FormValidationError className="company-form-validation-error" />}
          </div>
        </Container>
      </div>
    </Wrapper>
  );
};
